import { useState, useMemo } from 'react'
import { X, Search, Calendar, Clock, Hash } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'
import { useCreateAppearance } from '@/hooks/useAppearances'
import { formatDate } from '@/lib/date'
import { EmailPromptStep } from '@/components/episodes/EmailPromptStep'

interface EpisodeData {
  id: string
  number: number
  title: string
  recording_date: string
  recording_time: string | null
  air_date: string
  status: string
  meeting_link: string | null
}

interface CreatedAppearanceData {
  id: string
  episode: EpisodeData
}

interface InviteToEpisodeModalProps {
  isOpen: boolean
  onClose: () => void
  guestId: string
  guestName: string
  guestEmail: string | null
  existingEpisodeIds: string[]
  onSuccess?: () => void
}

export function InviteToEpisodeModal({
  isOpen,
  onClose,
  guestId,
  guestName,
  guestEmail,
  existingEpisodeIds,
  onSuccess,
}: InviteToEpisodeModalProps) {
  const modalKey = isOpen ? `${guestId}-open` : 'closed'

  if (!isOpen) return null

  return (
    <InviteToEpisodeModalContent
      key={modalKey}
      onClose={onClose}
      guestId={guestId}
      guestName={guestName}
      guestEmail={guestEmail}
      existingEpisodeIds={existingEpisodeIds}
      onSuccess={onSuccess}
    />
  )
}

interface InviteToEpisodeModalContentProps {
  onClose: () => void
  guestId: string
  guestName: string
  guestEmail: string | null
  existingEpisodeIds: string[]
  onSuccess?: () => void
}

function InviteToEpisodeModalContent({
  onClose,
  guestId,
  guestName,
  guestEmail,
  existingEpisodeIds,
  onSuccess,
}: InviteToEpisodeModalContentProps) {
  const { membership } = useAuth()
  const supabase = createClient()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<'select' | 'email-prompt'>('select')
  const [createdAppearance, setCreatedAppearance] = useState<CreatedAppearanceData | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedEpisode, setSelectedEpisode] = useState<EpisodeData | null>(null)
  const [note, setNote] = useState('')

  // Fetch all episodes for the team
  const { data: allEpisodes = [], isLoading } = useQuery({
    queryKey: ['episodes-for-invite', membership?.team_id],
    queryFn: async () => {
      if (!membership?.team_id) return []
      const { data } = await supabase
        .from('episodes')
        .select('id, number, title, recording_date, recording_time, air_date, status, meeting_link')
        .eq('team_id', membership.team_id)
        .order('recording_date', { ascending: true })
      return (data as EpisodeData[]) || []
    },
    enabled: !!membership?.team_id,
  })

  // Filter out episodes the guest is already on, and filter by search
  const existingEpisodeIdsSet = useMemo(
    () => new Set(existingEpisodeIds),
    [existingEpisodeIds]
  )

  // Calculate today once for reuse
  const today = useMemo(() => new Date().toISOString().split('T')[0], [])

  const availableEpisodes = useMemo(() => {
    const filtered = allEpisodes.filter((episode) => {
      if (existingEpisodeIdsSet.has(episode.id)) return false
      if (!searchQuery) return true
      const query = searchQuery.toLowerCase()
      return (
        episode.title.toLowerCase().includes(query) ||
        episode.number.toString().includes(query)
      )
    })

    // Sort: upcoming episodes first (recording_date >= today), then past episodes
    const upcoming = filtered.filter((e) => e.recording_date >= today)
    const past = filtered.filter((e) => e.recording_date < today)

    return [...upcoming, ...past]
  }, [allEpisodes, searchQuery, existingEpisodeIdsSet, today])

  // Create appearance mutation
  const createAppearance = useCreateAppearance()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedEpisode) return

    try {
      const appearance = await createAppearance.mutateAsync({
        guest_id: guestId,
        episode_id: selectedEpisode.id,
        recording_date: selectedEpisode.recording_date,
        recording_time: selectedEpisode.recording_time || undefined,
        note: note.trim() || undefined,
      })

      queryClient.invalidateQueries({ queryKey: ['guest-appearances', guestId] })

      // Store the created appearance data and transition to email prompt step
      setCreatedAppearance({
        id: appearance.id,
        episode: selectedEpisode,
      })
      setStep('email-prompt')
      onSuccess?.()
    } catch (error) {
      console.error('Failed to invite guest to episode:', error)
    }
  }

  const isSubmitting = createAppearance.isPending
  const canSubmit = !!selectedEpisode

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      draft: 'bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300',
      scheduled: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
      recorded: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400',
      published: 'bg-[#6369D1]/20 text-[#6369D1]',
    }
    return statusColors[status] || statusColors.draft
  }

  // Render email prompt step
  if (step === 'email-prompt' && createdAppearance) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div
          className="relative w-full max-w-lg mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <EmailPromptStep
            appearance={{ id: createdAppearance.id }}
            guest={{
              id: guestId,
              name: guestName,
              email: guestEmail,
            }}
            episode={{
              id: createdAppearance.episode.id,
              title: createdAppearance.episode.title,
              recording_date: createdAppearance.episode.recording_date,
              recording_time: createdAppearance.episode.recording_time,
              meeting_link: createdAppearance.episode.meeting_link,
            }}
            onClose={onClose}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="invite-to-episode-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} data-testid="invite-modal-backdrop" />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Invite to Episode
            </h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="invite-modal-guest-name">{guestName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            data-testid="invite-modal-close"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
            {/* Episode search */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                Select Episode <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by title or episode number..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="invite-episode-search"
                />
              </div>

              {/* Episode list */}
              <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-700" data-testid="invite-episode-list">
                {isLoading ? (
                  <div className="p-4 text-center text-sm text-zinc-500 dark:text-zinc-400" data-testid="invite-episodes-loading">
                    Loading episodes...
                  </div>
                ) : availableEpisodes.length === 0 ? (
                  <div className="p-4 text-center text-sm text-zinc-500 dark:text-zinc-400" data-testid="invite-episodes-empty">
                    {allEpisodes.length === 0
                      ? 'No episodes yet. Create one to get started.'
                      : searchQuery
                      ? 'No episodes match your search.'
                      : 'This guest is already on all episodes.'}
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
                    {availableEpisodes.map((episode) => {
                      const isUpcoming = episode.recording_date >= today
                      return (
                        <button
                          key={episode.id}
                          type="button"
                          onClick={() => setSelectedEpisode(episode)}
                          className={`w-full flex items-start gap-3 p-3 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors ${
                            selectedEpisode?.id === episode.id
                              ? 'bg-primary-50 dark:bg-primary-900/20 border-l-2 border-primary-500'
                              : ''
                          }`}
                          data-testid={`invite-episode-item-${episode.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="flex items-center gap-1 text-xs font-semibold text-[#FF6F00]">
                                <Hash className="w-3 h-3" />
                                {episode.number}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium capitalize ${getStatusBadge(episode.status)}`} data-testid={`invite-episode-status-${episode.id}`}>
                                {episode.status}
                              </span>
                            </div>
                            <p className="font-medium text-zinc-900 dark:text-zinc-100 truncate">
                              {episode.title}
                            </p>
                            <div className="flex items-center gap-3 mt-1.5">
                              <span className={`flex items-center gap-1 text-xs ${isUpcoming ? 'text-emerald-600 dark:text-emerald-400' : 'text-zinc-500 dark:text-zinc-400'}`}>
                                <Calendar className="w-3 h-3" />
                                {formatDate(episode.recording_date)}
                              </span>
                              {episode.recording_time && (
                                <span className="flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400">
                                  <Clock className="w-3 h-3" />
                                  {episode.recording_time}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Note */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                Note (optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Any notes about this appearance..."
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                data-testid="invite-note-input"
              />
            </div>

            {/* Error display */}
            {createAppearance.isError && (
              <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="invite-error">
                Failed to invite guest. Please try again.
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-200 dark:border-zinc-800 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
              data-testid="invite-cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="invite-submit-button"
            >
              {isSubmitting ? 'Inviting...' : 'Invite to Episode'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
